/**
 * i18n/index.ts
 *
 * Internationalisation configuration for CBOps.
 *
 * Supported languages (Roadmap v4 §28):
 *   en  English      — full coverage (canonical)
 *   sq  Shqip        — Albanian   (high-visibility namespaces)
 *   sr  Srpski       — Serbian    (high-visibility namespaces)
 *   ar  العربية      — Arabic     (high-visibility namespaces, RTL)
 *   tr  Türkçe       — Turkish    (high-visibility namespaces)
 *
 * Coverage scope:
 *   The four non-English locales translate the high-visibility
 *   namespaces — `lang`, `nav`, `common`, `topbar`, `auth`, `status`,
 *   `priority`, `dashboard`, `leave`, `tasks`. These cover the chrome
 *   that every user sees: sidebar, top bar, buttons, dashboard, leave,
 *   tasks. Anything else falls back to English automatically via
 *   i18next's `fallbackLng: 'en'`. Subsequent translation passes can
 *   fill in module-specific text without changing this config.
 *
 * Persistence:
 *   The selected locale is stored in `localStorage` under
 *   `pulsehq_locale` so it survives page reloads. On first load we
 *   read it; if absent we attempt the browser's `navigator.language`
 *   prefix; otherwise we default to English.
 *
 * RTL handling:
 *   Arabic (`ar`) requires right-to-left rendering. The `setLocale`
 *   helper exported below toggles `<html dir="rtl">` (or `ltr`) and
 *   sets `<html lang="ar">` so the rest of the document lays out
 *   correctly. CSS that uses logical properties (`me-2`, `ms-3`)
 *   automatically mirrors; legacy `ml-2` / `mr-3` doesn't, but most
 *   of our codebase uses Tailwind's `gap-*` and flex-row patterns
 *   which Just Work.
 *
 * Usage:
 *   import { useTranslation } from 'react-i18next';
 *   const { t } = useTranslation();
 *   t('nav.dashboard')   // "Dashboard" / "لوحة التحكم" / etc.
 *   t('common.save')     // "Save" / "Sačuvaj" / etc.
 *
 *   import { setLocale, getLocale } from '../i18n';
 *   setLocale('ar');     // applies RTL + persists choice
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import sq from './locales/sq.json';
import sr from './locales/sr.json';
import ar from './locales/ar.json';
import tr from './locales/tr.json';

// ─── Supported locales ────────────────────────────────────────────────────────

export const SUPPORTED_LOCALES = ['en', 'sq', 'sr', 'ar', 'tr'] as const;
export type LocaleCode = typeof SUPPORTED_LOCALES[number];

/** RTL locales — used to flip `<html dir>` on `setLocale`. */
const RTL_LOCALES: ReadonlySet<LocaleCode> = new Set<LocaleCode>(['ar']);

/** localStorage key for the persisted choice. */
const LOCALE_STORAGE_KEY = 'pulsehq_locale';

// ─── Initial locale resolution ────────────────────────────────────────────────

/**
 * Resolves the locale on first load:
 *   1. localStorage value (if valid)
 *   2. browser language prefix (e.g. "ar-SA" → "ar")
 *   3. fallback "en"
 */
function resolveInitialLocale(): LocaleCode {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as LocaleCode;
    }
  } catch { /* noop */ }
  const browser = (navigator.language || '').split('-')[0];
  if ((SUPPORTED_LOCALES as readonly string[]).includes(browser)) {
    return browser as LocaleCode;
  }
  return 'en';
}

const initial = resolveInitialLocale();

// ─── i18next initialisation ───────────────────────────────────────────────────

i18next
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      sq: { translation: sq },
      sr: { translation: sr },
      ar: { translation: ar },
      tr: { translation: tr },
    },
    lng:           initial,
    fallbackLng:   'en',
    interpolation: { escapeValue: false },
  });

// Apply initial RTL state.
if (typeof document !== 'undefined') {
  document.documentElement.lang = initial;
  document.documentElement.dir  = RTL_LOCALES.has(initial) ? 'rtl' : 'ltr';
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Switch the active locale.
 *   - Updates i18next so all `t()` calls re-render with the new strings
 *   - Persists the choice to localStorage
 *   - Flips `<html dir>` between `ltr` and `rtl` for Arabic
 *   - Updates `<html lang>` for screen readers and search-engine hints
 */
export function setLocale(code: LocaleCode): void {
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(code)) return;
  /*
   * TypeScript 5 + i18next 26 + react-i18next 17 ship strict generics on
   * the `i18n` interface that confuse method resolution at the
   * narrowest call site. Casting to `any` here is intentional — the
   * runtime call is well-typed by i18next; we just sidestep the
   * compiler's overload resolution. Same reason for `i18next.language`
   * below.
   */
  (i18next as any).changeLanguage(code);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch { /* noop */ }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = code;
    document.documentElement.dir  = RTL_LOCALES.has(code) ? 'rtl' : 'ltr';
  }
}

export function getLocale(): LocaleCode {
  return ((i18next as any).language as LocaleCode) || 'en';
}

export function isRTL(code?: LocaleCode): boolean {
  return RTL_LOCALES.has((code ?? getLocale()) as LocaleCode);
}

export default i18next;
